import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Ban, Check, Eye, EyeOff, Link2, Plus, Undo2, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from 'src/components/ui/dialog';
import { Button } from 'src/components/ui/button';
import { Input } from 'src/components/ui/input';
import { Textarea } from 'src/components/ui/textarea';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  SelectGroup,
  SelectLabel,
  SelectSeparator,
} from 'src/components/ui/select';
import { cn } from 'src/lib/utils';
import { healableCount } from './skillResolutionData';

const ACTION_META = {
  NEW_SKILL: {
    title: 'Create a new skill',
    sub: 'Adds it to the taxonomy, embeds it, then heals history.',
    icon: Plus,
    tint: 'bg-[#F3F0FF] text-[#7C3AED] dark:bg-[#251D40] dark:text-[#C4B5FD]',
    api: 'POST /admin/skill-resolution/resolve/new-skill',
    submitLabel: 'Create and heal',
  },
  ALIAS: {
    title: 'Add an alias',
    sub: 'Points this surface form at a skill you already have.',
    icon: Link2,
    tint: 'bg-brand-bg-subtle text-primary',
    api: 'POST /admin/skill-resolution/resolve/alias',
    submitLabel: 'Add alias and heal',
  },
  MAP: {
    title: 'Map history only',
    sub: 'Links the existing rows. Nothing changes in the taxonomy.',
    icon: Check,
    tint: 'bg-ds-success-subtle text-ds-success',
    api: 'POST /admin/skill-resolution/resolve/map',
    submitLabel: 'Heal these rows',
  },
  BLOCK: {
    title: 'Block this term',
    sub: 'Future parses scrub it. Existing rows are left alone.',
    icon: Ban,
    tint: 'bg-ds-danger-subtle text-ds-danger',
    api: 'POST /admin/skill-resolution/resolve/block',
    submitLabel: 'Add to blocklist',
  },
  IGNORE: {
    title: 'Hide from the queue',
    sub: 'Stays out of your way until you bring it back.',
    icon: EyeOff,
    tint: 'bg-brand-bg-subtle text-primary',
    api: 'POST /admin/skill-resolution/ignore',
    submitLabel: 'Ignore',
  },
  UNIGNORE: {
    title: 'Return to the queue',
    sub: 'Removes the ignore so it shows up in triage again.',
    icon: Eye,
    tint: 'bg-brand-bg-subtle text-primary',
    api: 'DELETE /admin/skill-resolution/ignore/{name_norm}',
    submitLabel: 'Unignore',
  },
  UNMAP: {
    title: 'Reverse this heal',
    sub: 'Only rows stamped BACKFILL go back to NONE.',
    icon: Undo2,
    tint: 'bg-ds-danger-subtle text-ds-danger',
    api: 'POST /admin/skill-resolution/unmap',
    submitLabel: 'Reverse heal',
  },
};

const normalize = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9+#. ]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

// `suggested` — the exact-hit and nearest-match candidates for this specific
// norm (from the record-detail endpoint), pinned in their own group above
// the full alphabetical taxonomy so the parser's own read on the norm is the
// first thing offered, not something buried at position 400 of 619.
function RefSkillSelect({ id, value, onChange, options, suggested, error }) {
  const suggestedIds = new Set(suggested.map((s) => s.id));
  const rest = options.filter((r) => !suggestedIds.has(r.id));
  return (
    <div className="space-y-1.5">
      <Select
        value={value ? String(value) : undefined}
        onValueChange={(v) => onChange(Number(v))}
      >
        <SelectTrigger id={id} className={cn(error && 'border-ds-danger')}>
          <SelectValue placeholder="Choose a reference skill…" />
        </SelectTrigger>
        <SelectContent side="bottom" avoidCollisions={false}>
          {suggested.length > 0 && (
            <>
              <SelectGroup>
                <SelectLabel>Suggested for this norm</SelectLabel>
                {suggested.map((r) => (
                  <SelectItem key={r.id} value={String(r.id)}>
                    {r.name} — id {r.id}
                    {r.tag ? ` · ${r.tag}` : ''}
                  </SelectItem>
                ))}
              </SelectGroup>
              <SelectSeparator />
            </>
          )}
          <SelectGroup>
            {suggested.length > 0 && (
              <SelectLabel>All reference skills</SelectLabel>
            )}
            {rest.map((r) => (
              <SelectItem key={r.id} value={String(r.id)}>
                {r.name} — id {r.id}
              </SelectItem>
            ))}
          </SelectGroup>
        </SelectContent>
      </Select>
      {error && (
        <p className="text-[11.5px] font-semibold text-ds-danger">{error}</p>
      )}
    </div>
  );
}
RefSkillSelect.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })
  ).isRequired,
  suggested: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      tag: PropTypes.string,
    })
  ),
  error: PropTypes.string,
};
RefSkillSelect.defaultProps = { value: null, suggested: [], error: null };

// Alias/blocked-term tag input — Enter adds a chip.
function TagInput({ id, tags, onChange, placeholder }) {
  const [draft, setDraft] = useState('');
  return (
    <div>
      <Input
        id={id}
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            const v = draft.trim();
            if (v && !tags.includes(v)) {
              onChange([...tags, v]);
              setDraft('');
            }
          }
        }}
      />
      {tags.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md bg-brand-bg-subtle text-primary text-[11.5px] font-semibold font-mono"
            >
              {t}
              <button
                type="button"
                aria-label={`Remove ${t}`}
                onClick={() => onChange(tags.filter((x) => x !== t))}
                className="opacity-70 hover:opacity-100"
              >
                <X className="w-[11px] h-[11px]" strokeWidth={2.4} />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
TagInput.propTypes = {
  id: PropTypes.string.isRequired,
  tags: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
};
TagInput.defaultProps = { placeholder: '' };

function FieldLabel({ htmlFor, children, required }) {
  return (
    <label
      htmlFor={htmlFor}
      className="text-[12.5px] font-semibold text-foreground-strong block mb-1.5"
    >
      {children}
      {required && <span className="text-ds-danger ml-0.5">*</span>}
    </label>
  );
}
FieldLabel.propTypes = {
  htmlFor: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  required: PropTypes.bool,
};
FieldLabel.defaultProps = { required: false };

const SkillActionDialog = ({
  open,
  onClose,
  action,
  item,
  presetTargetId,
  refSkills,
  onSubmit,
}) => {
  const meta = action ? ACTION_META[action] : null;
  const topVariant = item?.variants?.length ? item.variants[0][0] : item?.norm;
  const sortedRefSkills = useMemo(
    () => refSkills.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [refSkills]
  );

  // Real, norm-specific candidates from the queue-detail endpoint — the
  // exact hit (if any) first, then the nearest-match distribution, deduped
  // by id and capped so it stays a shortlist, not another full list.
  const suggestedTargets = useMemo(() => {
    if (!item) return [];
    const list = [];
    const seen = new Set();
    if (item.exact) {
      list.push({
        id: item.exact.id,
        name: item.exact.name,
        tag: 'exact match',
      });
      seen.add(item.exact.id);
    }
    (item.nearest || []).forEach(([nid, nname, , score]) => {
      if (seen.has(nid) || list.length >= 5) return;
      seen.add(nid);
      list.push({
        id: nid,
        name: nname,
        tag:
          typeof score === 'number'
            ? `${Math.round(score * 100)}% match`
            : null,
      });
    });
    return list;
  }, [item]);

  const [name, setName] = useState('');
  const [type, setType] = useState('DOMAIN_SKILL');
  const [description, setDescription] = useState('');
  const [extraAliases, setExtraAliases] = useState([]);
  const [heal, setHeal] = useState(true);
  const [targetId, setTargetId] = useState(null);
  const [blockedTerm, setBlockedTerm] = useState('');
  const [reason, setReason] = useState('NOISE');
  const [notes, setNotes] = useState('');
  const [reasonText, setReasonText] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    setName(topVariant || '');
    setType('DOMAIN_SKILL');
    setDescription('');
    setExtraAliases(item.variants?.slice(1).map((v) => v[0]) || []);
    setHeal(true);
    setTargetId(
      presetTargetId ||
        (item.exact ? item.exact.id : item.nearest?.[0]?.[0] || null)
    );
    setBlockedTerm(topVariant || '');
    setReason('NOISE');
    setNotes('');
    setReasonText('');
    setError(null);
    setSubmitting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item, action]);

  if (!open || !action || !meta || !item) return null;

  const Icon = meta.icon;
  const healable = healableCount(item);

  const buildBody = () => {
    switch (action) {
      case 'NEW_SKILL':
        return {
          name_norm: item.norm,
          name,
          type,
          description: description || null,
          extra_aliases: extraAliases,
          heal,
        };
      case 'ALIAS':
        return { name_norm: item.norm, ref_skill_id: targetId, heal };
      case 'MAP':
        return { name_norm: item.norm, ref_skill_id: targetId };
      case 'BLOCK':
        return {
          name_norm: item.norm,
          reason,
          notes,
          // Optional — defaults server-side to the most frequent raw
          // variant when omitted, so only send it if it's been customised.
          ...(blockedTerm.trim() ? { blocked_term: blockedTerm.trim() } : {}),
        };
      case 'IGNORE':
        return { name_norm: item.norm, reason: reasonText };
      case 'UNIGNORE':
        return { name_norm: item.norm };
      case 'UNMAP':
        return { name_norm: item.norm, ref_skill_id: targetId };
      default:
        return {};
    }
  };

  const handleSubmit = async () => {
    if (action === 'NEW_SKILL') {
      if (!name.trim()) {
        setError('Give the skill a name.');
        return;
      }
      const clash = refSkills.find(
        (r) => normalize(r.name) === normalize(name)
      );
      if (clash) {
        setError(
          `"${clash.name}" already exists (id ${clash.id}). Add an alias to it instead of creating a duplicate.`
        );
        return;
      }
    }
    if (action === 'ALIAS' && !targetId) {
      setError('Choose the skill this is an alias of.');
      return;
    }
    if ((action === 'MAP' || action === 'UNMAP') && !targetId) {
      setError('Choose a reference skill.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      await onSubmit(action, buildBody());
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-[540px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <span
              className={cn(
                'w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
                meta.tint
              )}
            >
              <Icon className="w-4 h-4" strokeWidth={2} />
            </span>
            <div className="min-w-0">
              <DialogTitle>{meta.title}</DialogTitle>
              <DialogDescription>{meta.sub}</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {error && (
            <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-ds-danger-subtle text-ds-danger text-[12.5px] font-medium">
              {error}
            </div>
          )}

          <div>
            <FieldLabel htmlFor="f-norm">Normalised name</FieldLabel>
            <Input
              id="f-norm"
              value={item.norm}
              readOnly
              className="font-mono bg-muted"
            />
            <p className="text-[11.5px] text-muted-foreground mt-1.5">
              Sent as <code className="font-mono">name_norm</code>. The server
              re-normalises and rejects anything that disagrees.
            </p>
          </div>

          {action === 'NEW_SKILL' && (
            <>
              <div>
                <FieldLabel htmlFor="f-name" required>
                  Canonical name
                </FieldLabel>
                <Input
                  id="f-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <p className="text-[11.5px] text-muted-foreground mt-1.5">
                  How the skill appears everywhere else in HireEdge.
                </p>
              </div>
              <div>
                <FieldLabel htmlFor="f-type" required>
                  Type
                </FieldLabel>
                <Select value={type} onValueChange={setType}>
                  <SelectTrigger id="f-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DOMAIN_SKILL">DOMAIN_SKILL</SelectItem>
                    <SelectItem value="TOOL">TOOL</SelectItem>
                    <SelectItem value="REGULATORY">REGULATORY</SelectItem>
                    <SelectItem value="SOFT_SKILL">SOFT_SKILL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel htmlFor="f-description">Description</FieldLabel>
                <Textarea
                  id="f-description"
                  rows={2}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="One line — it improves the embedding"
                />
              </div>
              <div>
                <FieldLabel htmlFor="f-alias-in">Extra aliases</FieldLabel>
                <TagInput
                  id="f-alias-in"
                  tags={extraAliases}
                  onChange={setExtraAliases}
                  placeholder="Type a form and press Enter"
                />
                <p className="text-[11.5px] text-muted-foreground mt-1.5">
                  Created as validated aliases. Orthographic variants are seeded
                  automatically on top of these.
                </p>
              </div>
              <label
                htmlFor="f-heal"
                className="flex items-start gap-2.5 text-[12.5px] text-muted-foreground"
              >
                <input
                  id="f-heal"
                  type="checkbox"
                  checked={heal}
                  onChange={(e) => setHeal(e.target.checked)}
                  className="mt-0.5 accent-primary"
                />
                <span>
                  Heal the {healable} existing rows now{' '}
                  <span className="text-muted-foreground/80">
                    (uncheck to create the skill without touching history)
                  </span>
                </span>
              </label>
            </>
          )}

          {action === 'ALIAS' && (
            <>
              <div>
                <FieldLabel htmlFor="f-target" required>
                  Alias of
                </FieldLabel>
                <RefSkillSelect
                  id="f-target"
                  value={targetId}
                  onChange={setTargetId}
                  options={sortedRefSkills}
                  suggested={suggestedTargets}
                />
                <p className="text-[11.5px] text-muted-foreground mt-1.5">
                  The skill these rows should point at.
                </p>
              </div>
              <label
                htmlFor="f-heal"
                className="flex items-center gap-2.5 text-[12.5px] text-muted-foreground"
              >
                <input
                  id="f-heal"
                  type="checkbox"
                  checked={heal}
                  onChange={(e) => setHeal(e.target.checked)}
                  className="accent-primary"
                />
                Heal the {healable} existing rows now
              </label>
            </>
          )}

          {action === 'MAP' && (
            <div>
              <FieldLabel htmlFor="f-target" required>
                Link rows to
              </FieldLabel>
              <RefSkillSelect
                id="f-target"
                value={targetId}
                onChange={setTargetId}
                options={sortedRefSkills}
                suggested={suggestedTargets}
              />
              {!item.exact && (
                <div className="mt-2.5 px-3 py-2.5 rounded-lg bg-ds-warning-subtle text-[12.5px] text-foreground-strong">
                  <strong className="font-semibold">History only.</strong> No
                  alias is created, so the next parse of this exact form
                  resolves only if the exact or embedding rung already catches
                  it. For a form that keeps coming back, add an alias instead.
                </div>
              )}
            </div>
          )}

          {action === 'BLOCK' && (
            <>
              <div>
                <FieldLabel htmlFor="f-blocked-term">Blocked term</FieldLabel>
                <Input
                  id="f-blocked-term"
                  value={blockedTerm}
                  onChange={(e) => setBlockedTerm(e.target.value)}
                />
                <p className="text-[11.5px] text-muted-foreground mt-1.5">
                  Optional — the display form stored on the blocklist row.
                  Defaults to the most frequent raw variant if left blank.
                </p>
              </div>
              <div>
                <FieldLabel htmlFor="f-reason" required>
                  Reason
                </FieldLabel>
                <Select value={reason} onValueChange={setReason}>
                  <SelectTrigger id="f-reason">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="NOISE">
                      NOISE — generic non-skill
                    </SelectItem>
                    <SelectItem value="PROPRIETARY">
                      PROPRIETARY — employer-internal tool
                    </SelectItem>
                    <SelectItem value="DOMAIN">
                      DOMAIN — not a skill in this domain
                    </SelectItem>
                    <SelectItem value="INDUSTRY">
                      INDUSTRY — industry label, not a skill
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel htmlFor="f-notes">Notes</FieldLabel>
                <Input
                  id="f-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Why this is not a skill"
                />
              </div>
              <div className="px-3 py-2.5 rounded-lg bg-brand-bg-subtle text-[12.5px] text-foreground-strong">
                The {healable} rows already carrying this term are left as they
                are. They stay visible under status{' '}
                <strong className="font-semibold">Blocked</strong>.
              </div>
            </>
          )}

          {action === 'IGNORE' && (
            <>
              <div>
                <FieldLabel htmlFor="f-reason-text">Reason</FieldLabel>
                <Input
                  id="f-reason-text"
                  value={reasonText}
                  onChange={(e) => setReasonText(e.target.value)}
                  placeholder="e.g. parse artifact, too rare to matter"
                />
              </div>
              <div className="px-3 py-2.5 rounded-lg bg-brand-bg-subtle text-[12.5px] text-foreground-strong">
                Occurrences keep accumulating while it is ignored — it just
                stays out of the default queue until you unignore it.
              </div>
            </>
          )}

          {action === 'UNIGNORE' && (
            <div className="px-3 py-2.5 rounded-lg bg-brand-bg-subtle text-[12.5px] text-foreground-strong">
              This norm returns to the open queue with all {healable} rows
              intact. The original ignore stays in the audit trail.
            </div>
          )}

          {action === 'UNMAP' && (
            <>
              <div>
                <FieldLabel htmlFor="f-target" required>
                  Reverse rows healed to
                </FieldLabel>
                <RefSkillSelect
                  id="f-target"
                  value={targetId}
                  onChange={setTargetId}
                  options={sortedRefSkills}
                  suggested={suggestedTargets}
                />
              </div>
              <div className="px-3 py-2.5 rounded-lg bg-ds-warning-subtle text-[12.5px] text-foreground-strong">
                <strong className="font-semibold">
                  Only BACKFILL rows are reversed.
                </strong>{' '}
                Parser-made links are left alone, and the nearest match and
                score are preserved. If the mapping itself was wrong, deactivate
                the alias too — otherwise the next parse re-links these rows.
              </div>
            </>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button
            variant={
              action === 'BLOCK' || action === 'UNMAP'
                ? 'destructive'
                : 'default'
            }
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? 'Saving…' : meta.submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

SkillActionDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  action: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  item: PropTypes.object,
  presetTargetId: PropTypes.number,
  refSkills: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number, name: PropTypes.string })
  ).isRequired,
  onSubmit: PropTypes.func.isRequired,
};
SkillActionDialog.defaultProps = {
  action: null,
  item: null,
  presetTargetId: null,
};

export default SkillActionDialog;
